import { CommonModule, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GouvDvfDataDto, PropertyDto, PropertyType, UserApplications } from '@workspace/data-types';
import { LuxonDatePipe } from '@workspace/ng-aura';
import { ApplicationStore, UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, of, tap } from 'rxjs';
import { EtxeEnumDisplayPipe } from '../../../../pipes/etxe-enum-display.pipe';

import { PropertyDvfGaugeComponent } from '../../../property-dvf-gauge/property-dvf-gauge.component';

@Component({
  selector: 'etxe-property-view-headline',
  imports: [
    CommonModule,
    DecimalPipe,
    MatIconModule,
    MatTooltipModule,
    LuxonDatePipe,
    EtxeEnumDisplayPipe,
    PropertyDvfGaugeComponent,
  ],
  templateUrl: './property-view-headline.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block',
  },
})
export class PropertyViewHeadlineComponent {
  property = input.required<PropertyDto>();

  private readonly destroyRef = inject(DestroyRef);
  private apiService = inject(ApiService);
  private readonly appStore = inject(ApplicationStore);
  private readonly userStore = inject(UserStore);

  readonly isAdminApp = computed(() => this.appStore.name() === UserApplications.ADMIN);
  private fetchedDvfData = signal<GouvDvfDataDto | null>(null);
  private lastFetchedDvfKey: string | null = null;

  constructor() {
    effect(() => {
      const prop = this.property();
      if (prop.geospatial?.dvf || this.fetchedDvfData()) {
        return;
      }
      const loc = prop.location;
      const codeInsee = loc?.address?.cityCode;
      const type = prop.definition?.propertyType || PropertyType.APARTMENT;
      const lat = loc?.coordinates?.latitude;
      const lon = loc?.coordinates?.longitude;

      if (!codeInsee) return;

      const key = `${codeInsee}_${type}_${lat}_${lon}`;
      if (this.lastFetchedDvfKey === key) return;
      this.lastFetchedDvfKey = key;

      untracked(() => {
        this.fetchDvfData(codeInsee, type, lat, lon);
      });
    });
  }

  private fetchDvfData(codeInsee?: string, type?: string, lat?: number, lon?: number) {
    if (!codeInsee || !type) return;

    this.apiService
      .business()
      .etxe()
      .public()
      .gouv()
      .dvf(codeInsee, type, lat, lon)
      .pipe(
        catchError(() => of(null)),
        tap((res) => {
          if (res) {
            this.fetchedDvfData.set(res);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  sellingPriceFormatted = computed<string>(() => {
    const priceCents = this.property().financials?.sellingPriceCents;
    if (!priceCents) return '-';
    return (priceCents / 100).toLocaleString('fr-FR');
  });

  pricePerSquareMeterFormatted = computed<string | null>(() => {
    const priceCents = this.property().financials?.sellingPriceCents;
    const surface = this.property().keyInfo?.surface?.habitable;
    if (!priceCents || !surface || surface <= 0) return null;
    return Math.round(priceCents / 100 / surface).toLocaleString('fr-FR');
  });

  currentPriceM2 = computed<number | null>(() => {
    const priceCents = this.property().financials?.sellingPriceCents;
    const surface = this.property().keyInfo?.surface?.habitable;
    if (!priceCents || !surface || surface <= 0) return null;
    return priceCents / 100 / surface;
  });

  dvfData = computed<GouvDvfDataDto | null>(() => {
    return this.property().geospatial?.dvf ?? this.fetchedDvfData();
  });

  notaryFeesFormatted = computed<string | null>(() => {
    const notaryCents = this.property().financials?.taxesAndFees?.notaryFeesCents;
    if (!notaryCents) return null;
    return (notaryCents / 100).toLocaleString('fr-FR');
  });

  notaryFeesPercentageFormatted = computed<string | null>(() => {
    const notaryCents = this.property().financials?.taxesAndFees?.notaryFeesCents;
    const priceCents = this.property().financials?.sellingPriceCents;
    if (!notaryCents || !priceCents || priceCents === 0) return null;
    const pct = (notaryCents / priceCents) * 100;
    return pct.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  });
}
